<template>
  <div class="flex gap-2">
    <Dropdown
      ref="dropdownRef"
      v-model="subPropertyId"
      :options="subPropertiesOptions"
      option-label="label"
      option-value="value"
      :option-disabled="optionDisabled"
      :placeholder="t('segmentation.complex.propertyPlaceholder')"
      class="flex input-height"
      @change="resetPropertyInput"
      @before-show="onBeforeShow"
      @before-hide="onBeforeHide"
    >
      <template #option="slotProps">
        <div class="flex align-items-center justify-content-between w-full" v-tooltip="slotProps.option.isCustomAttribute ? t('segmentation.custom.tooltip') : null">
          <div class="flex align-items-center">
            <i v-if="slotProps.option.isCustomAttribute" class="fa-solid fa-puzzle-piece custom-icon mr-2"></i>
            <i v-if="isPropertyOverridden(slotProps.option.value)" class="fa-solid fa-layer-group override-icon mr-2"></i>
            <i v-if="slotProps.option.isAwaited" class="fa-regular fa-clock awaited-icon mr-2"></i>
            <span>{{ slotProps.option.label }}</span>
          </div>
          <div class="flex align-items-center gap-1">
            <span v-if="slotProps.option.isCustomAttribute" class="custom-badge">CUSTOM</span>
            <span v-if="isPropertyOverridden(slotProps.option.value)" class="override-badge">OVERRIDE</span>
            <i v-if="isPropertyOverridden(slotProps.option.value)" class="fa-regular fa-circle-question help-icon" @click.stop="(e) => openOverridePanel(e, slotProps.option.value)"></i>
          </div>
        </div>
      </template>
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex align-items-center justify-content-between w-full">
          <div class="flex align-items-center">
            <i v-if="getSelectedOptionIsCustom(slotProps.value)" class="fa-solid fa-puzzle-piece custom-icon mr-2"></i>
            <i v-if="isPropertyOverridden(slotProps.value)" class="fa-solid fa-layer-group override-icon mr-2"></i>
            <i v-if="isAwaitedVisitDuration(propertyDefinition.id, slotProps.value)" class="fa-regular fa-clock awaited-icon mr-2"></i>
            <span>{{ getSelectedOptionLabel(slotProps.value) }}</span>
          </div>
          <div class="flex align-items-center gap-1">
            <span v-if="getSelectedOptionIsCustom(slotProps.value)" class="custom-badge-small">CUSTOM</span>
            <span v-if="isPropertyOverridden(slotProps.value)" class="override-badge-small">OVERRIDE</span>
            <i v-if="isPropertyOverridden(slotProps.value)" class="fa-regular fa-circle-question help-icon-small" @click.stop="(e) => openOverridePanel(e, slotProps.value)"></i>
          </div>
        </div>
        <span v-else class="dropdown-placeholder">{{ t('segmentation.complex.propertyPlaceholder') }}</span>
      </template>
    </Dropdown>
  </div>
  <OverlayPanel ref="overridePanelRef">
    <OverrideInfoPanel
      v-if="currentOverrideField"
      :system-field-name="currentOverrideField"
    />
  </OverlayPanel>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, ref, SetupContext, Ref
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import OverlayPanel from 'primevue/overlaypanel';
import OverrideInfoPanel from '@/components/segmentations/OverrideInfoPanel.vue';
import { useI18n } from "vue-i18n";
import { store } from "@/store";
import validateSegmentProperty from '@/components/segmentations/utils/utils';
import useProductOverrides from '@/composables/segmentations/useProductOverrides';
import { isAwaitedVisitDuration } from '@/composables/segmentations/triggerSegmentation';

export default defineComponent({
  name: 'SegmentationComplexPropertyDropdown',

  components: { Dropdown, OverlayPanel, OverrideInfoPanel },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    propertyConfig: {
      type: Object,
      required: true,
    },

    propertyDefinition: {
      type: Object,
      required: true,
    },

    property: {
      type: String,
      required: true
    },

    index: {
      type: Number,
      required: true,
    }
  },

  emits: ['update:property', 'reset-property-input'],


  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();
    const subPropertyId = ref(props.property);

    const optionDisabled = ref('');
    const overridePanelRef = ref();
    const currentOverrideField = ref<string | null>(null);
    const { loadOverrides, hasOverrides } = useProductOverrides();

    const subPropertiesOptions = computed(() =>
      Object.entries(props.propertyDefinition.sub_properties).map(([key, values]: [string, any]) => {
        const config = props.propertyConfig.sub_properties.filter((property: any) => property.id);
        const isMultiple = 'multiple' in values && values.multiple;
        const disabled = config.find((property: any) => property.id === values.id);
        return {
          label: t(values.label),
          value: values.id,
          disabled: disabled !== undefined && !isMultiple && disabled.id !== subPropertyId.value,
          isCustomAttribute: values.isCustomAttribute || false,
          isAwaited: isAwaitedVisitDuration(props.propertyDefinition.id, values.id),
        };
      })
    );

    const currentSegmentConfig = computed(() => store.getters['segmentationsEditor/getCurrentSegmentConfig']);

    // Helper function to check if selected option is custom
    const getSelectedOptionIsCustom = (value: string) => {
      const option = subPropertiesOptions.value.find(opt => opt.value === value);
      return option?.isCustomAttribute || false;
    };

    // Helper function to get selected option label
    const getSelectedOptionLabel = (value: string) => {
      const option = subPropertiesOptions.value.find(opt => opt.value === value);
      return option?.label || '';
    };

    const refreshComplexPropertyComponent = () => {
      emit('refreshComponent');
    }

    const resetPropertyInput = () => {
      emit('update:property', subPropertyId.value);
      emit('reset-property-input');
    }

    const dropdownRef = ref();
    const onBeforeShow = async () => {
      optionDisabled.value = 'disabled';
      if (currentSegmentConfig.value) {
        if (currentSegmentConfig.value.configProperty.segmentId !== props.propertyConfig.sub_properties[props.index].segmentId) {
          const { segmentId } = currentSegmentConfig.value.configProperty;
          const validation = await validateSegmentProperty(currentSegmentConfig.value.configProperty, currentSegmentConfig.value.propertyDefinition);
          if (validation && !validation.success) {
            store.commit('segmentationsEditor/pushError', { type: 'segments', value: { segmentId, value: validation.validate.value } });
            dropdownRef.value.hide();
            return false;
          }
          store.commit('segmentationsEditor/removeError', { type: 'segments', id: 'segmentId', value: segmentId });
          store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
          store.commit('segmentationsEditor/setRefreshContactsNumber', true);
        }
      } else {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
      }
    };

    const onBeforeHide = () => {
      optionDisabled.value = '';
    };

    /**
     * Check if a property is overridden
     *
     * @param propertyId - The ID of the property to check
     * @returns true if the property is overridden, false otherwise
     */
    const isPropertyOverridden = (propertyId: string) => {
      return hasOverrides(propertyId, props.propertyConfig.id);
    };

    const openOverridePanel = (event: Event, propertyId: string) => {
      currentOverrideField.value = propertyId;
      overridePanelRef.value.toggle(event);
    };

    return {
      t,
      subPropertiesOptions,
      dropdownRef,
      subPropertyId,
      optionDisabled,
      resetPropertyInput,
      refreshComplexPropertyComponent,
      onBeforeShow,
      onBeforeHide,
      getSelectedOptionIsCustom,
      getSelectedOptionLabel,
      isAwaitedVisitDuration,
      isPropertyOverridden,
      overridePanelRef,
      openOverridePanel,
      currentOverrideField,
    };
  },
});
</script>

<style lang="scss" scoped>
.custom-icon {
  color: #8b5cf6;
  font-size: 0.875rem;
}

.custom-badge {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.custom-badge-small {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.55rem;
  font-weight: 600;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.awaited-icon {
  color: #d97706;
  font-size: 0.875rem;
}

.dropdown-placeholder {
  color: #6b7280;
  font-style: italic;
}

.override-icon {
  color: #f59e0b;
  font-size: 0.875rem;
}

.override-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.override-badge-small {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  font-size: 0.55rem;
  font-weight: 600;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.help-icon {
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f59e0b;
  }
}

.help-icon-small {
  color: #6b7280;
  font-size: 0.75rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f59e0b;
  }
}
</style>

